import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { addSalesperson, getSalespeople, removeSalesperson, reactivateSalesPerson, updateSalespersonDetails } from '../../lib/api';
import type { SalesPersonInfo } from '../../lib/api';

const STATUS_COLOR: Record<string, { bg: string; color: string }> = {
  Active:   { bg: '#f0fdf4', color: '#16a34a' },
  Inactive: { bg: '#f9fafb', color: '#9ca3af' },
};

// Sales CRM roles: a Lead Manager or Sales Admin gets leads only with "Also takes leads"
const ROLES = ['Salesperson', 'Lead Manager', 'Sales Admin'] as const;
const ROLE_HINT: Record<string, string> = {
  'Salesperson':  'Calls leads: Today, My Leads, Follow-ups',
  'Lead Manager': 'Adds leads, sets the rules, moves leads, sees the team',
  'Sales Admin':  'Everything the Lead Manager does, plus the sales reports',
};

const RoleFields: React.FC<{ role: string; takesLeads: boolean; onRole: (r: string) => void; onTakes: (t: boolean) => void }> = ({ role, takesLeads, onRole, onTakes }) => (
  <div className="space-y-2">
    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide">Role in the sales CRM</label>
    <div className="grid grid-cols-3 gap-2">
      {ROLES.map(r => (
        <button key={r} type="button" onClick={() => onRole(r)}
          className="py-2 rounded-xl text-xs font-bold border-2 transition-all"
          style={role === r ? { background: '#312741', color: '#fff', borderColor: '#312741' } : { background: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb' }}>
          {r}
        </button>
      ))}
    </div>
    <p className="text-[11px] text-gray-400">{ROLE_HINT[role]}</p>
    {role !== 'Salesperson' && (
      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
        <input type="checkbox" checked={takesLeads} onChange={e => onTakes(e.target.checked)} className="w-4 h-4 accent-purple-700" />
        Also takes leads (gets leads in turn and calls them)
      </label>
    )}
  </div>
);

const ManageSalespeople: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const [people, setPeople]     = useState<SalesPersonInfo[]>([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState({ name: '', passcode: '', email: '', phone: '', joiningDate: '', birthday: '', role: 'Salesperson', takesLeads: false });
  const [showPass, setShowPass] = useState(false);
  // Email is their app sign-in — editable so typos can be fixed
  const [editing, setEditing]   = useState<SalesPersonInfo | null>(null);
  const [editForm, setEditForm] = useState({ email: '', phone: '', role: 'Salesperson', takesLeads: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSalespeople(adminPassword);
      setPeople(res.salespeople);
    } catch (err: any) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name required'); return; }
    if (!form.passcode.trim()) { toast.error('Passcode required'); return; }
    if (!form.email.trim()) { toast.error('Email required — they sign in to the app with it'); return; }
    setSaving(true);
    try {
      const res = await addSalesperson(adminPassword, form);
      toast.success(`${form.name} added — ID: ${res.salesPersonId}`);
      setForm({ name: '', passcode: '', email: '', phone: '', joiningDate: '', birthday: '', role: 'Salesperson', takesLeads: false });
      setShowForm(false);
      load();
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const openEdit = (sp: SalesPersonInfo) => {
    setEditForm({ email: sp.email || '', phone: sp.phone || '', role: sp.role || 'Salesperson', takesLeads: sp.role !== 'Salesperson' && !!sp.takesLeads });
    setEditing(sp);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    try {
      await updateSalespersonDetails(adminPassword, editing.salesPersonId, {
        email: editForm.email.trim(), phone: editForm.phone.trim(), role: editForm.role, takesLeads: editForm.takesLeads,
      });
      toast.success(`${editing.name} updated`);
      setEditing(null);
      load();
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async (sp: SalesPersonInfo) => {
    if (!window.confirm(`Deactivate ${sp.name}? Their open CRM leads go to the other salespeople.`)) return;
    try {
      const res = await removeSalesperson(adminPassword, sp.salesPersonId);
      toast.success(`${sp.name} deactivated${res.released ? ` · ${res.released} leads handed to others` : ''}`);
      load();
    } catch (err: any) { toast.error(err.message); }
  };

  const handleActivate = async (sp: SalesPersonInfo) => {
    try {
      await reactivateSalesPerson(adminPassword, sp.salesPersonId);
      toast.success(`${sp.name} reactivated`);
      load();
    } catch (err: any) { toast.error(err.message); }
  };

  return (
    <TrackerLayout>
      <div className="space-y-5 max-w-2xl">

        {/* Header */}
        <div className="rounded-2xl p-5 flex items-center justify-between" style={{ background: 'linear-gradient(135deg,#1e1433,#312741)' }}>
          <div>
            <h1 className="text-xl font-black text-white">Manage Salespeople</h1>
            <p className="text-white/50 text-sm mt-0.5">{people.filter(p => p.status === 'Active').length} active · {people.length} total</p>
          </div>
          <button onClick={() => setShowForm(true)}
            className="px-4 py-2 rounded-xl text-sm font-black shadow hover:scale-105 active:scale-95 transition-transform"
            style={{ background: '#F0B429', color: '#312741' }}>
            + Add Salesperson
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin" />
          </div>
        ) : people.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-4xl mb-3">👔</p>
            <p className="text-gray-500 font-semibold">No salespeople yet</p>
            <p className="text-gray-400 text-sm mt-1">Click "Add Salesperson" to create the first account</p>
          </div>
        ) : (
          <div className="space-y-3">
            {people.map(sp => {
              const sc = STATUS_COLOR[sp.status] || STATUS_COLOR.Active;
              return (
                <div key={sp.salesPersonId} className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-4 shadow-sm">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg flex-shrink-0"
                    style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                    {sp.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-gray-800">{sp.name}</p>
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#ede9fe', color: '#7c3aed' }}>{sp.salesPersonId}</span>
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.color }}>{sp.status}</span>
                      {sp.role && sp.role !== 'Salesperson' && (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#fef3c7', color: '#92400e' }}>
                          {sp.role}{sp.takesLeads ? ' · takes leads' : ''}
                        </span>
                      )}
                      {sp.crmPaused && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Paused in CRM</span>}
                    </div>
                    <div className="flex gap-3 mt-0.5 text-xs text-gray-400 flex-wrap">
                      {sp.email && <span>✉ {sp.email}</span>}
                      {sp.phone && <span>📞 {sp.phone}</span>}
                      {sp.joiningDate && <span>📅 Joined {sp.joiningDate}</span>}
                      {sp.birthday && <span>🎂 {sp.birthday}</span>}
                    </div>
                    {sp.passcode && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Passcode</span>
                        <code className="text-xs font-bold px-2 py-0.5 rounded-lg bg-gray-100 text-gray-800 select-all">{sp.passcode}</code>
                        <button onClick={() => navigator.clipboard?.writeText(sp.passcode || '').then(() => toast.success('Passcode copied'), () => {})}
                          className="text-[11px] font-semibold text-purple-600 hover:underline">Copy</button>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => openEdit(sp)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-purple-200 text-purple-600 hover:bg-purple-50 transition-colors font-semibold">
                      Edit
                    </button>
                    {sp.status === 'Active' ? (
                      <button onClick={() => handleDeactivate(sp)}
                        className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors font-semibold">
                        Deactivate
                      </button>
                    ) : (
                      <button onClick={() => handleActivate(sp)}
                        className="text-xs px-3 py-1.5 rounded-lg border border-green-200 text-green-600 hover:bg-green-50 transition-colors font-semibold">
                        Activate
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowForm(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden">
            <div className="px-6 py-4" style={{ background: 'linear-gradient(135deg,#1e1433,#312741)' }}>
              <div className="flex items-center justify-between">
                <p className="text-white font-black text-base">Add Salesperson</p>
                <button onClick={() => setShowForm(false)} className="text-white/50 hover:text-white text-2xl leading-none">×</button>
              </div>
              <p className="text-white/50 text-xs mt-0.5">A login ID (SP01, SP02…) will be auto-generated</p>
            </div>
            <form onSubmit={handleAdd} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Full Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required
                  placeholder="e.g. Rahul Sharma"
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Passcode (Login Password) *</label>
                <div className="relative">
                  <input value={form.passcode} onChange={e => setForm(f => ({ ...f, passcode: e.target.value }))} required
                    type={showPass ? 'text' : 'password'}
                    placeholder="Min 4 characters"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400 pr-12" />
                  <button type="button" onClick={() => setShowPass(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-bold">
                    {showPass ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Email (app login) *</label>
                  <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required
                    type="email" placeholder="name@gmail.com"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Phone</label>
                  <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="optional"
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Joining Date</label>
                  <input type="date" value={form.joiningDate} onChange={e => setForm(f => ({ ...f, joiningDate: e.target.value }))}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Birthday</label>
                  <input type="date" value={form.birthday} onChange={e => setForm(f => ({ ...f, birthday: e.target.value }))}
                    className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400" />
                </div>
              </div>
              <RoleFields role={form.role} takesLeads={form.takesLeads}
                onRole={r => setForm(f => ({ ...f, role: r }))} onTakes={t => setForm(f => ({ ...f, takesLeads: t }))} />
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving}
                  className="flex-1 py-2.5 rounded-xl text-sm font-black text-white disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg,#312741,#5a4a6f)' }}>
                  {saving ? 'Creating…' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden">
            <div className="px-6 py-4" style={{ background: 'linear-gradient(135deg,#1e1433,#312741)' }}>
              <div className="flex items-center justify-between">
                <p className="text-white font-black text-base">Edit {editing.name}</p>
                <button onClick={() => setEditing(null)} className="text-white/50 hover:text-white text-2xl leading-none">×</button>
              </div>
              <p className="text-white/50 text-xs mt-0.5">They sign in to the app with this email and their passcode</p>
            </div>
            <form onSubmit={handleEdit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Email (app login)</label>
                <input value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} required
                  type="email" placeholder="name@gmail.com"
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Phone</label>
                <input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="optional"
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm outline-none focus:border-purple-400" />
              </div>
              <RoleFields role={editForm.role} takesLeads={editForm.takesLeads}
                onRole={r => setEditForm(f => ({ ...f, role: r }))} onTakes={t => setEditForm(f => ({ ...f, takesLeads: t }))} />
              <p className="text-[11px] text-gray-400">A new role shows the next time they sign in.</p>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setEditing(null)}
                  className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving}
                  className="flex-1 py-2.5 rounded-xl text-sm font-black text-white disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg,#312741,#5a4a6f)' }}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </TrackerLayout>
  );
};

export default ManageSalespeople;
